import { apiBlob, apiClient, apiClientPaginated } from '@/lib/api-client';
import type {
  BillWithItems,
  BillListFilters,
  BillsListResponse,
  BillListItem,
  InitiateReturnInput,
  InitiateReturnResult,
} from './types';

function buildListQuery(filters: BillListFilters): string {
  const q = new URLSearchParams();
  if (filters.page) q.set('page', String(filters.page));
  if (filters.limit) q.set('limit', String(filters.limit));
  if (filters.status) q.set('status', filters.status);
  if (filters.locationId) q.set('location_id', filters.locationId);
  if (filters.startDate) q.set('start_date', filters.startDate);
  if (filters.endDate) q.set('end_date', filters.endDate);
  if (filters.paymentMethod) q.set('payment_method', filters.paymentMethod);
  if (filters.search) q.set('search', filters.search);
  if (filters.customerName) q.set('customer_name', filters.customerName);
  if (filters.customerPhone) q.set('customer_phone', filters.customerPhone);
  if (filters.billNumber) q.set('bill_number', filters.billNumber);
  if (filters.customerId) q.set('customer_id', filters.customerId);
  const s = q.toString();
  return s ? `?${s}` : '';
}

/**
 * REST client for the bill detail surface. Pure pass-through to the
 * existing /api/v1/bills/... endpoints — no business logic here.
 *
 * PDF endpoints are exposed as URL builders rather than fetchers because
 * the browser handles the application/pdf response natively (open in a
 * new tab, native print dialog, native Save As).
 */
export const billsApi = {
  /**
   * Paginated list with backend-side filtering.
   *
   * NOTE: the paginated endpoint returns `{ statusCode, data, meta }` where
   * `meta` lives at the envelope's top level (hoisted by ResponseInterceptor).
   * We MUST use `apiClientPaginated` here — the plain `apiClient` strips
   * meta and returns just `data`, which would make pagination silently fail.
   */
  async list(filters: BillListFilters = {}): Promise<BillsListResponse> {
    const result = await apiClientPaginated<BillListItem[]>(`/bills${buildListQuery(filters)}`);
    return { data: result.data, meta: result.meta as BillsListResponse['meta'] };
  },

  /**
   * Every bill the list filters match, as CSV (T20) — all pages, under the
   * same location scope as the list. Page and page size don't apply.
   */
  exportCsv(filters: BillListFilters = {}) {
    return apiBlob(`/bills/export${buildListQuery({ ...filters, page: undefined, limit: undefined })}`);
  },

  /** Bill header + line items + per-tender payments. */
  getById(id: string) {
    return apiClient<BillWithItems>(`/bills/${id}`);
  },

  /** Void a DRAFT (parked) bill. The backend rejects non-draft bills. */
  void(id: string) {
    return apiClient<BillWithItems>(`/bills/${id}/void`, { method: 'PATCH' });
  },

  /** Initiate a return for a COMPLETED bill → creates a credit note. */
  initiateReturn(id: string, input: InitiateReturnInput) {
    return apiClient<InitiateReturnResult>(`/bills/${id}/return`, {
      method: 'POST',
      body: input,
    });
  },

  /** URLs that the browser opens directly. */
  invoicePdfUrl(id: string) {
    return `/api/v1/bills/${id}/invoice`;
  },
  receiptPdfUrl(id: string) {
    return `/api/v1/bills/${id}/receipt`;
  },
  receipt58mmPdfUrl(id: string) {
    return `/api/v1/bills/${id}/receipt/58mm`;
  },
  creditNotePdfUrl(id: string) {
    return `/api/v1/bills/${id}/credit-note`;
  },
};
